import React, { useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import BoardModal, { BMCancelButton, BMDangerButton, BM_LABEL, BM_INPUT } from './BoardModal';
import { deleteProject } from '../services/projectService';

interface Props {
  pca: IPublicClientApplication;
  projectId: string;
  projectName: string;
  onDeleted: () => void;
  onCancel: () => void;
}

export default function DeleteProjectModal({ pca, projectId, projectName, onDeleted, onCancel }: Props) {
  const [typed, setTyped] = useState('');
  const [working, setWorking] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  const ready = typed === projectName;

  const confirm = async () => {
    if (!ready || working) return;
    setWorking(true); setInfo(null);
    try {
      await deleteProject(pca, projectId);
      onDeleted();
    } catch (e) {
      const msg = e instanceof Error
        ? e.message
        : 'Project deletion requires cascade delete configuration — contact your administrator.';
      setInfo(msg);
    } finally {
      setWorking(false);
    }
  };

  return (
    <BoardModal
      open
      title="Delete project"
      onClose={onCancel}
      footer={<>
        <BMCancelButton onClick={onCancel} disabled={working} />
        <BMDangerButton onClick={confirm} disabled={!ready || working}>
          {working ? 'Deleting…' : 'Delete Project'}
        </BMDangerButton>
      </>}
    >
      <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14 }}>
        Deleting <strong>{projectName}</strong> will permanently remove the project
        and all of its tasks, collections, issues, risks, milestones, decisions,
        comments, attachments, and time entries. This cannot be undone.
      </div>
      <div style={{ marginBottom: 8 }}>
        <label style={BM_LABEL}>
          Type the project name to confirm
        </label>
        <input
          autoFocus
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder={projectName}
          style={BM_INPUT}
          disabled={working}
        />
      </div>
      {info && (
        <div style={{
          marginTop: 12,
          padding: '8px 10px',
          background: '#FEF3C7',
          borderLeft: '3px solid #D97706',
          color: '#92400E',
          fontSize: 12,
          borderRadius: 4,
        }}>{info}</div>
      )}
    </BoardModal>
  );
}
